import {
  type SwappedInboundMessage,
  type SwappedSellOrderData,
  isValidOrderData,
} from '@funkit/connect-core'
import { useCallback } from 'react'

import { logger } from '../logging/logger'
import {
  type SwappedScreenBaseProps,
  SwappedScreenShell,
} from './SwappedScreenShell'

export interface SwappedWithdrawScreenProps extends SwappedScreenBaseProps {
  /** "Make another withdrawal" — back to the method list. */
  onNewWithdrawal?: () => void
  /** Swapped confirmed the fiat payout side. Payload is opaque. */
  onWithdrawalSuccess?: (data?: unknown) => void
  /** Sell order (deposit address + accepted-token amount). Only validated payloads forward. */
  onOrderData?: (order: SwappedSellOrderData) => void
  /** Chain of the accepted token; the deposit address must belong to it. */
  targetChainId: string
}

/**
 * Withdrawal-mode host for the Swapped embed (mirroring web's
 * `WithdrawalSwappedIframeController`): back → {@link onBack},
 * close → {@link onClose}, new-withdrawal → {@link onNewWithdrawal},
 * withdrawal-successful → {@link onWithdrawalSuccess}, order-data →
 * {@link onOrderData}. Deposit-only messages are ignored — see
 * `SwappedScreen`.
 */
export function SwappedWithdrawScreen({
  url,
  onClose,
  onBack,
  onNewWithdrawal,
  onWithdrawalSuccess,
  onOrderData,
  targetChainId,
}: SwappedWithdrawScreenProps) {
  const handleMessage = useCallback(
    (msg: SwappedInboundMessage) => {
      switch (msg.type) {
        case 'SWAPPED_GO_BACK':
          logger.info('connect-rn:swapped:back')
          onBack()
          break
        case 'SWAPPED_CLOSE':
          logger.info('connect-rn:swapped:exit')
          onClose()
          break
        case 'SWAPPED_NEW_WITHDRAWAL':
          logger.info('connect-rn:swapped:new_withdrawal')
          onNewWithdrawal?.()
          break
        case 'SWAPPED_WITHDRAWAL_SUCCESSFUL':
          // No auto-close: the embed shows its own success screen with
          // new-withdrawal/close controls (web parity).
          logger.info('connect-rn:swapped:withdrawal_successful')
          onWithdrawalSuccess?.(msg.data)
          break
        case 'SWAPPED_ORDER_DATA':
          if (!isValidOrderData(msg.data, targetChainId)) {
            // Validation rejection, not an unrecoverable failure (WARN — the
            // embed keeps its own UI in control).
            logger.warn('connect-rn:swapped:order_data:invalid', {
              data: msg.data ?? {},
              targetChainId,
            })
            break
          }
          logger.info('connect-rn:swapped:order_data', {
            order_id: msg.data.order_id,
          })
          onOrderData?.(msg.data)
          break
        default:
          // READY / NAVIGATION / LOGOUT / ERROR handled in the shell or
          // tracked elsewhere; deposit-only messages (new-deposit,
          // transaction-successful) belong to SwappedScreen.
          break
      }
    },
    [
      onBack,
      onClose,
      onNewWithdrawal,
      onWithdrawalSuccess,
      onOrderData,
      targetChainId,
    ],
  )

  return (
    <SwappedScreenShell url={url} onMessage={handleMessage} onBack={onBack} />
  )
}
